import { Show, type VoidComponent } from "solid-js";
import posthog from "posthog-js";
import { useTranslation } from "../../../../state/i18n";
import { directUserToSupport } from "../../../../utils/supportUtil";
import { IconButtonMenu, type IconButtonMenuProps } from "../../../../components/listItemMenu/ListItemMenu";
import { useHubSpotState } from "../../../../hubSpot";
import { useCustomerState } from "../../../../state/customer";
import LegacyPlatformService from "../../../../services/LegacyPlatformService";
import { POSTHOG_EVENTS } from "../../../../services/PostHogService";

type HelpMenuProps = {
  triggerSize: "medium" | "large";
  menu?: IconButtonMenuProps<string>["menu"];
  showExitBeta: boolean;
};
const HelpMenu: VoidComponent<HelpMenuProps> = (props) => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const hubSpotState = useHubSpotState();
  const legacyPlatformOverviewUrl = LegacyPlatformService.createLegacyPlatformOverviewUrl(customer.customerId);

  return (
    <div class="relative">
      <Show when={hubSpotState.unreadMessages > 0}>
        <div
          class="absolute -top-1.5 -right-1.5 rounded-sm"
          role="status"
          aria-label={t(($) => $.HELP_MENU_UNREAD_MESSAGES_ARIA_LABEL)}
        >
          <svg
            class="icon-status-danger"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="7" cy="7" r="6" fill="currentColor" stroke="white" stroke-width="2" />
          </svg>
        </div>
      </Show>

      <IconButtonMenu
        aria-label={t(($) => $.HELP_MENU_ARIA_LABEL)}
        triggerIcon="questionMarkCircle"
        triggerSize={props.triggerSize}
        menu={props.menu}
        items={[
          [
            {
              value: "help-center",
              type: "link",
              href: "https://helpcenter.moreapp.com",
              target: "_blank",
              variant: "text",
              label: t(($) => $.HELP_MENU_ACTIONS_NAVIGATE_TO_HELP_CENTER),
            },
            {
              value: "developer-docs",
              type: "link",
              href: "https://docs.moreapp.com/docs/developer-docs",
              target: "_blank",
              variant: "text",
              label: t(($) => $.HELP_MENU_ACTIONS_NAVIGATE_TO_DEVELOPER_DOCS),
            },
          ],
          [
            {
              value: "submit-feedback",
              type: "link",
              href: "https://links.moreapp.com/platform-feedback",
              target: "_blank",
              variant: "text",
              label: t(($) => $.HELP_MENU_ACTIONS_NAVIGATE_TO_FEEDBACK_FORM),
            },
            {
              value: "contact-support",
              type: "action",
              onClick: () => directUserToSupport(),
              variant: "text",
              label: t(($) => $.HELP_MENU_ACTIONS_CONTACT_SUPPORT),
              ...(hubSpotState.unreadMessages > 0
                ? {
                    pill: {
                      variant: "danger",
                      inverted: true,
                      label: hubSpotState.unreadMessages.toString(),
                    },
                  }
                : {}),
            },
            {
              value: "support-tickets",
              type: "link",
              href: "https://meet.moreapp.com/support-tickets",
              target: "_blank",
              variant: "text",
              label: t(($) => $.HELP_MENU_ACTIONS_SUPPORT_TICKETS),
            },
          ],
          props.showExitBeta
            ? [
                {
                  value: "exit-beta",
                  type: "link",
                  href: legacyPlatformOverviewUrl,
                  variant: "text",
                  label: t(($) => $.HELP_MENU_ACTIONS_SWITCH_TO_OLD_PLATFORM),
                  onClick: () => posthog.capture(POSTHOG_EVENTS.LEGACY_PLATFORM_OPENED, { source: "help_menu" }),
                },
              ]
            : [],
        ]}
      />
    </div>
  );
};

export default HelpMenu;
